import { useEffect } from 'react';
import type { CSSProperties } from 'react';
import type { ReactNode } from 'react';
import {
  Button,
  Col,
  DatePicker,
  Form,
  Input,
  Row,
  Select,
  Space,
  Typography,
} from 'antd';
import type { FormInstance } from 'antd';
import { useCatalogos } from '../../catalogos/api/useCatalogos';
import { useT } from '../../../locales';
import {
  blankStringToNull,
  CATEGORIA_TIPO_SUSCRIPCION,
  type ClienteResponse,
  type CreateClienteDto,
  type UpdateClienteDto,
} from '@aid/shared';
import type { Dayjs } from 'dayjs';
import { parseDateOnly, parseFechaAlta, toDateOnlyString } from '../../../lib/date';
import { derivarDiaCobro } from '../cliente-financiero';
import { AjusteCambioDiaSection } from './AjusteCambioDiaSection';
import { MapaUbicacionPicker, type UbicacionCoords } from '../../../components/MapaUbicacionPicker';

const itemStyle: CSSProperties = { marginBottom: 4 };

const CLIENT_OPTIONAL_TEXT_FIELDS = [
  'identificacion',
  'telefonoPrincipal',
  'telefonoReferencia',
  'email',
  'ip',
  'nit',
  'direccion',
  'comentarios',
] as const;

export interface ClienteFormValues {
  nombre?: string;
  identificacion?: string | null;
  telefonoPrincipal?: string | null;
  telefonoReferencia?: string | null;
  email?: string | null;
  ip?: string | null;
  nit?: string | null;
  direccion?: string | null;
  latitud?: number | null;
  longitud?: number | null;
  comentarios?: string | null;
  diaCobro?: number | null;
  estadoCliente?: number | null;
  tipoCliente?: number | null;
  tipoSuscripcion?: number | null;
  fechaNacimiento?: Dayjs | null;
  fechaCobro?: Dayjs | null;
  fechaAlta?: Dayjs | null;
  /** Opt-in flag for the billing-day-change adjustment (edit mode only). */
  aplicarAjuste?: boolean;
}

function normalizeClientFormValues(values: ClienteFormValues): ClienteFormValues {
  const normalized = { ...values };
  for (const field of CLIENT_OPTIONAL_TEXT_FIELDS) {
    normalized[field] = blankStringToNull(normalized[field]);
  }
  return normalized;
}

function normalizeOptionalNumber(value: unknown): number | undefined {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : undefined;
  }

  if (typeof value !== 'string' || value.trim() === '') {
    return undefined;
  }

  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
}

function formatDate(value: Dayjs | null | undefined): string | undefined {
  return toDateOnlyString(value);
}

function formatNullableDate(value: Dayjs | null | undefined): string | null | undefined {
  if (value === null) return null;
  return formatDate(value);
}

function toUpdateClienteDto(values: ClienteFormValues): UpdateClienteDto {
  return {
    nombre: values.nombre,
    fechaNacimiento: formatNullableDate(values.fechaNacimiento),
    identificacion: values.identificacion,
    telefonoPrincipal: values.telefonoPrincipal,
    telefonoReferencia: values.telefonoReferencia,
    email: values.email,
    ip: values.ip,
    tipoCliente: normalizeOptionalNumber(values.tipoCliente),
    tipoSuscripcion: normalizeOptionalNumber(values.tipoSuscripcion),
    estadoCliente: normalizeOptionalNumber(values.estadoCliente),
    nit: values.nit,
    direccion: values.direccion,
    latitud: values.latitud,
    longitud: values.longitud,
    comentarios: values.comentarios,
    fechaCobro: formatDate(values.fechaCobro),
    fechaAlta: formatNullableDate(values.fechaAlta),
    // `diaCobro` is intentionally NOT sent: the hidden-field contract makes
    // the backend derive the monthly anchor from `fechaCobro` (deriveDiaCobro),
    // preserving a 29/30/31 clamp. The form store keeps the value only so the
    // dirty snapshot can compare the fetched client against the edit draft.
  };
}

function toCreateClienteDto(values: ClienteFormValues): CreateClienteDto | null {
  const nombre = values.nombre;
  const estadoCliente = normalizeOptionalNumber(values.estadoCliente);
  const fechaCobro = formatDate(values.fechaCobro);

  if (nombre === undefined || nombre.length === 0 || estadoCliente === undefined || !fechaCobro) {
    return null;
  }

  return {
    ...toUpdateClienteDto(values),
    nombre,
    estadoCliente,
    fechaCobro,
  };
}

interface FormSectionHeadingProps {
  children: ReactNode;
  first?: boolean;
}

function FormSectionHeading({ children, first = false }: FormSectionHeadingProps) {
  return (
    <Col xs={24}>
      <Typography.Title
        level={5}
        style={{
          margin: first ? '0 0 4px' : '16px 0 4px',
          fontSize: 14,
          lineHeight: 1.4,
        }}
      >
        {children}
      </Typography.Title>
    </Col>
  );
}

export function getClienteFormInitialValues(initialValues: ClienteResponse): ClienteFormValues {
  return {
    nombre: initialValues.nombre ?? '',
    identificacion: initialValues.identificacion ?? '',
    telefonoPrincipal: initialValues.telefonoPrincipal ?? '',
    telefonoReferencia: initialValues.telefonoReferencia ?? '',
    email: initialValues.email ?? '',
    ip: initialValues.ip ?? '',
    nit: initialValues.nit ?? '',
    direccion: initialValues.direccion ?? '',
    latitud: initialValues.ubicacion?.latitud ?? null,
    longitud: initialValues.ubicacion?.longitud ?? null,
    comentarios: initialValues.comentarios ?? '',
    diaCobro: initialValues.diaCobro,
    estadoCliente: initialValues.estadoCliente?.id,
    tipoCliente: initialValues.tipoCliente?.id,
    tipoSuscripcion: initialValues.tipoSuscripcion?.id,
    fechaNacimiento: parseDateOnly(initialValues.fechaNacimiento) ?? undefined,
    fechaCobro: parseDateOnly(initialValues.fechaCobro) ?? undefined,
    // fechaAlta is a timestamptz INSTANT on the wire, but the edit field is
    // date-only (legacy business value = calendar day). parseFechaAlta
    // recovers UTC-midnight legacy values as their UTC calendar date and
    // preserves real instants (post-0012 Guatemala-midnight values) — a raw
    // dayjs() would render a legacy 2026-01-05T00:00:00.000Z as Jan 4 in
    // America/Guatemala.
    fechaAlta: parseFechaAlta(initialValues.fechaAlta) ?? undefined,
  };
}

interface ClienteFormCommonProps {
  initialValues?: ClienteResponse | null;
  loading?: boolean;
  form?: FormInstance<ClienteFormValues>;
  hideSubmit?: boolean;
  onValuesChange?: (
    changedValues: Partial<ClienteFormValues>,
    allValues: ClienteFormValues,
  ) => void;
}

interface ClienteCreateFormProps extends ClienteFormCommonProps {
  mode: 'create';
  onSubmit: (values: CreateClienteDto) => void;
}

interface ClienteEditFormProps extends ClienteFormCommonProps {
  mode: 'edit';
  onSubmit: (values: UpdateClienteDto) => void;
}

type ClienteFormProps = ClienteCreateFormProps | ClienteEditFormProps;

export function ClienteForm(props: ClienteFormProps) {
  const {
    mode,
    initialValues,
    loading,
    form: externalForm,
    hideSubmit = false,
    onValuesChange,
  } = props;
  const t = useT();
  const [internalForm] = Form.useForm<ClienteFormValues>();
  const form = externalForm ?? internalForm;
  const { data: estados } = useCatalogos('ESTADO_CLIENTE');
  const { data: tipos } = useCatalogos('TIPO_CLIENTE');
  const { data: suscripciones } = useCatalogos(CATEGORIA_TIPO_SUSCRIPCION);
  const latitud = Form.useWatch('latitud', form) as number | null | undefined;
  const longitud = Form.useWatch('longitud', form) as number | null | undefined;
  const fechaCobroValue = Form.useWatch('fechaCobro', form) as Dayjs | null | undefined;
  // Feedback mirrors the backend contract: an untouched date keeps the
  // persisted anchor; a real date move derives the new one (29/30/31 clamp
  // preserved). `diaCobro` is never sent, so the server stays authoritative.
  // Normalize the fetched value (legacy ISO-midnight included) to a bare
  // YYYY-MM-DD so the "untouched date" comparison is timezone-safe.
  const fechaCobroInicial = formatDate(parseDateOnly(initialValues?.fechaCobro)) ?? null;
  const fechaCobroActual = toDateOnlyString(fechaCobroValue) ?? null;
  const diaCobroMostrado =
    fechaCobroInicial !== null &&
    toDateOnlyString(fechaCobroValue) === fechaCobroInicial &&
    initialValues?.diaCobro != null
      ? initialValues.diaCobro
      : derivarDiaCobro(fechaCobroValue, initialValues?.diaCobro ?? null);
  const ubicacion: UbicacionCoords | null =
    latitud != null && longitud != null ? { lat: latitud, lng: longitud } : null;

  // No se usa `initialValues` del Form solo porque la data llega async
  // (useCliente) y el store del form se crea antes del primer render con data:
  // initialValues solo aplica al mount del store, no cuando la query resuelve.
  // El efecto corre en cada mount (el modal usa destroyOnHidden, que remonta
  // el formulario) y pisa valores stale del store persistente.
  useEffect(() => {
    if (initialValues) {
      form.setFieldsValue(getClienteFormInitialValues(initialValues));
    }
  }, [initialValues, form]);

  // Default de negocio: el alta de un cliente nuevo es un cliente ACTIVO (editable).
  // Se aplica solo en create y solo si el campo sigue vacío (no pisa una elección previa).
  useEffect(() => {
    if (mode !== 'create' || !estados || estados.length === 0) return;
    const activo = estados.find((e) => e.key === 'ACTIVO');
    if (!activo) return;
    const current = form.getFieldValue('estadoCliente');
    if (current === undefined || current === null) {
      form.setFieldValue('estadoCliente', activo.id);
    }
  }, [estados, mode, form]);

  // New clients default to the MENSUAL catalog entry. Existing clients are
  // backfilled by the migration, so editing never overwrites a persisted value.
  useEffect(() => {
    if (mode !== 'create') return;
    const mensual = suscripciones?.find((entry) => entry.key === 'MENSUAL');
    if (!mensual) return;
    const current = form.getFieldValue('tipoSuscripcion');
    if (current === undefined || current === null) {
      form.setFieldValue('tipoSuscripcion', mensual.id);
    }
  }, [suscripciones, mode, form]);

  const handleUbicacionChange = (value: UbicacionCoords | null) => {
    form.setFieldsValue({ latitud: value?.lat ?? null, longitud: value?.lng ?? null });
  };

  // Keep the form representation separate from the API representation: Ant
  // Design owns Dayjs values while the API receives ISO date strings.
  const handleFinish = (values: ClienteFormValues) => {
    const normalizedValues = normalizeClientFormValues(values);
    if (props.mode === 'create') {
      const payload = toCreateClienteDto(normalizedValues);
      if (payload) props.onSubmit(payload);
      return;
    }

    props.onSubmit(toUpdateClienteDto(normalizedValues));
  };

  return (
    <Form<ClienteFormValues>
      form={form}
      layout="vertical"
      onFinish={handleFinish}
      onValuesChange={onValuesChange}
      scrollToFirstError={{ focus: true }}
      style={{ width: '100%' }}
    >
      <Row gutter={[16, 0]}>
        <FormSectionHeading first>{t('pages.clientes.form.identity')}</FormSectionHeading>
        <Col xs={24}>
          <Form.Item
            style={itemStyle}
            name="nombre"
            label={t('pages.clientes.name')}
            rules={[{ required: true, message: t('pages.clientes.nameRequired') }]}
          >
            <Input autoFocus />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12}>
          <Form.Item style={itemStyle} name="ip" label={t('pages.clientes.codigo')}>
            <Input />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12}>
          <Form.Item
            style={itemStyle}
            name="identificacion"
            label={t('pages.clientes.identificacion')}
          >
            <Input />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12}>
          <Form.Item style={itemStyle} name="nit" label={t('pages.clientes.nit')}>
            <Input />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12}>
          <Form.Item style={itemStyle} name="tipoCliente" label={t('pages.clientes.tipo')}>
            <Select
              allowClear
              style={{ width: '100%' }}
              placeholder={t('pages.clientes.selectTipo')}
              options={(tipos ?? []).map((tipo) => ({
                label: tipo.value ?? tipo.key ?? `#${tipo.id}`,
                value: tipo.id,
              }))}
            />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12}>
          <Form.Item
            style={itemStyle}
            name="tipoSuscripcion"
            label={t('pages.clientes.tipoSuscripcion')}
          >
            <Select
              style={{ width: '100%' }}
              allowClear={false}
              placeholder={t('pages.clientes.selectTipoSuscripcion')}
              options={(suscripciones ?? []).map((suscripcion) => ({
                label: suscripcion.value ?? suscripcion.key ?? `#${suscripcion.id}`,
                value: suscripcion.id,
              }))}
            />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12}>
          <Form.Item
            style={itemStyle}
            name="estadoCliente"
            label={t('pages.clientes.status')}
            rules={
              mode === 'create'
                ? [{ required: true, message: t('pages.clientes.statusRequired') }]
                : undefined
            }
          >
            <Select
              style={{ width: '100%' }}
              placeholder={t('pages.clientes.selectEstado')}
              options={(estados ?? []).map((e) => ({
                label: e.value ?? e.key ?? `#${e.id}`,
                value: e.id,
              }))}
            />
          </Form.Item>
        </Col>

        <FormSectionHeading>{t('pages.clientes.form.contact')}</FormSectionHeading>
        <Col xs={24} sm={12}>
          <Form.Item style={itemStyle} name="telefonoPrincipal" label={t('pages.clientes.phone')}>
            <Input />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12}>
          <Form.Item
            style={itemStyle}
            name="telefonoReferencia"
            label={t('pages.clientes.phoneReference')}
          >
            <Input />
          </Form.Item>
        </Col>
        <Col xs={24}>
          <Form.Item
            style={itemStyle}
            name="email"
            label={t('pages.clientes.email')}
            rules={[
              {
                type: 'email',
                transform: (value: unknown) =>
                  typeof value === 'string' && value.trim() === '' ? undefined : value,
                message: t('pages.clientes.invalidEmail'),
              },
            ]}
          >
            <Input placeholder={t('pages.clientes.emailPlaceholder')} />
          </Form.Item>
        </Col>

        <FormSectionHeading>{t('pages.clientes.form.location')}</FormSectionHeading>
        <Col xs={24}>
          <Form.Item style={itemStyle} name="direccion" label={t('pages.clientes.direccion')}>
            <Input placeholder={t('pages.clientes.direccionPlaceholder')} />
          </Form.Item>
        </Col>
        <Col xs={24}>
          {/* Hidden fields keep the picker coordinates inside Ant Design's form store. */}
          <Form.Item name="latitud" hidden>
            <Input />
          </Form.Item>
          <Form.Item name="longitud" hidden>
            <Input />
          </Form.Item>
          <MapaUbicacionPicker
            value={ubicacion}
            onChange={handleUbicacionChange}
            disabled={loading}
          />
        </Col>

        <FormSectionHeading>{t('pages.clientes.form.billing')}</FormSectionHeading>
        {/* Hidden-field contract: the operator picks only the billing date;
            the anchor ("Día de Factura") is derived server-side and shown
            here as read-only feedback. */}
        <Col xs={24} sm={12}>
          <Form.Item
            style={itemStyle}
            name="fechaCobro"
            label={t('pages.clientes.fechaCobro')}
            extra={
              diaCobroMostrado != null
                ? t('pages.clientes.diaCobroDerivado', { dia: diaCobroMostrado })
                : undefined
            }
            rules={
              mode === 'create'
                ? [{ required: true, message: t('pages.clientes.fechaCobroRequired') }]
                : undefined
            }
          >
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
        </Col>
        {/* Adjustment is edit-only: a new client has no previous period. */}
        {mode === 'edit' &&
        initialValues != null &&
        fechaCobroInicial !== null &&
        fechaCobroActual !== null &&
        fechaCobroActual !== fechaCobroInicial ? (
          <AjusteCambioDiaSection
            clienteId={initialValues.id}
            fechaCobroNueva={fechaCobroActual}
          />
        ) : null}

        <FormSectionHeading>{t('pages.clientes.form.dates')}</FormSectionHeading>
        <Col xs={24} sm={mode === 'edit' ? 12 : 24}>
          <Form.Item
            style={itemStyle}
            name="fechaNacimiento"
            label={t('pages.clientes.fechaNacimiento')}
          >
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
        </Col>
        {/* Solo en edición: la fecha de alta es un dato histórico que se ajusta; al crear, el alta es hoy. */}
        {mode === 'edit' && (
          <Col xs={24} sm={12}>
            <Form.Item style={itemStyle} name="fechaAlta" label={t('pages.clientes.fechaAlta')}>
              <DatePicker style={{ width: '100%' }} />
            </Form.Item>
          </Col>
        )}
        <FormSectionHeading>{t('pages.clientes.form.notes')}</FormSectionHeading>
        <Col xs={24}>
          <Form.Item style={itemStyle} name="comentarios" label={t('pages.clientes.comments')}>
            <Input.TextArea rows={3} />
          </Form.Item>
        </Col>
        {!hideSubmit && (
          <Col xs={24}>
            <Form.Item style={itemStyle}>
              <Space wrap size={[8, 8]}>
                <Button type="primary" htmlType="submit" loading={loading}>
                  {mode === 'create' ? t('pages.clientes.create') : t('common.saveChanges')}
                </Button>
              </Space>
            </Form.Item>
          </Col>
        )}
      </Row>
    </Form>
  );
}
